// cSpell:enableCompoundWords
import { ASDropdown, ASTextField, ComponentSize } from '@eleventheye/asui';
import { NFLConference, NFLDivision } from '@shared/constants';
import { FieldTurfType, TeamAbrvID } from '@shared/types';
import TitledButtonGroup from 'components/button/group/components/TitledButtonGroup';
import TeamPicker from 'components/teampicker';
import React from 'react';

import OutlineTitlePanel from 'components/outlinetitlepanel';
import styles from './EditTeam.module.scss';

type EditTeamProps = {
  className?: string;
  teamFullName: string;
  setTeamFullName: (val: string) => void;
  teamCity: string;
  setTeamCity: (val: string) => void;
  teamState: string;
  setTeamState: (val: string) => void;
  teamStadium: string;
  setTeamStadium: (val: string) => void;
  stadiumCapacity: number;
  setStadiumCapacity: (val: number) => void;
  selectedConference: NFLConference | undefined;
  setSelectedConference: (conf: NFLConference) => void;
  selectedDivision: NFLDivision | undefined;
  setSelectedDivision: (div: NFLDivision) => void;
  teamStadiumHasRoof: boolean;
  setTeamStadiumHasRoof: (flag: boolean) => void;
  teamStadiumFieldType: FieldTurfType;
  setTeamStadiumFieldType: (turf: FieldTurfType) => void;
  teamRivalries?: TeamAbrvID[];
  setTeamRivalries?: (rivalries: TeamAbrvID[]) => void;
  disabledTeams?: TeamAbrvID[];
};

const conferenceOpts = Object.keys(NFLConference) as NFLConference[];
const divisionOpts = Object.values(NFLDivision) as NFLDivision[];
const afcDivisions = divisionOpts.filter((div) => div.includes('AFC'));
const nfcDivisions = divisionOpts.filter((div) => div.includes('NFC'));
const fieldTypeOpts = ['ArtificialTurf', 'NaturalGrass'] as FieldTurfType[];
const stadiumTypeOpts = ['Open', 'Roof / Dome'];

const US_STATES = [
  '--- State ---',
  'Arizona',
  'California',
  'Colorado',
  'D.C.',
  'Florida',
  'Georgia',
  'Illinois',
  'Indiana',
  'Louisiana',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Missouri',
  'Nevada',
  'New Jersey',
  'New York',
  'North Carolina',
  'Ohio',
  'Pennsylvania',
  'Tennessee',
  'Texas',
  'Washington',
  'Wisconsin',
];

const EditTeam: React.FC<EditTeamProps> = ({
  className = '',
  teamFullName,
  setTeamFullName,
  teamCity,
  setTeamCity,
  teamState,
  setTeamState,
  teamStadium,
  setTeamStadium,
  stadiumCapacity,
  setStadiumCapacity,
  selectedConference,
  setSelectedConference,
  selectedDivision,
  setSelectedDivision,
  teamStadiumHasRoof,
  setTeamStadiumHasRoof,
  teamStadiumFieldType,
  setTeamStadiumFieldType,
  teamRivalries,
  setTeamRivalries,
  disabledTeams,
}) => {
  const baseCls = `${styles.EditTeamContainer} ${className}`;
  return (
    <div className={baseCls}>
      <OutlineTitlePanel title="Team Details" className={styles.EditTeamOutlinePanel}>
        <div className={styles.EditTeamGroup}>
          <ASTextField
            value={teamFullName}
            showClearButton
            placeholder="Team Full Name"
            size={ComponentSize.Medium}
            onChange={setTeamFullName}
            // style={{ minWidth: 'unset', maxWidth: '235px', width: '100%' }}
          />
        </div>

        <div className={styles.EditTeamGroup}>
          <ASTextField
            value={teamCity}
            showClearButton
            placeholder="City"
            size={ComponentSize.Medium}
            onChange={setTeamCity}
            // style={{ maxWidth: '250px', minWidth: 'unset', width: '100%' }}
          />

          <ASDropdown
            id="SelectStatesId"
            onChange={setTeamState}
            options={US_STATES}
            value={teamState ? teamState : US_STATES[0]}
            size={ComponentSize.Medium}
            // style={{ minWidth: '175px', maxWidth: '250px', marginBottom: '0', width: 'unset' }}
          />
        </div>

        <div className={styles.EditTeamGroup}>
          <TitledButtonGroup
            title="Conference"
            buttonItems={conferenceOpts}
            onItemSelected={(conf) => setSelectedConference(conf as NFLConference)}
            selectedItem={selectedConference}
            itemSize={ComponentSize.Medium}
          />
          <TitledButtonGroup
            title="Division"
            buttonItems={selectedConference && selectedConference === NFLConference.AFC ? afcDivisions : nfcDivisions}
            selectedItem={selectedDivision}
            onItemSelected={(div) => setSelectedDivision(div as NFLDivision)}
            itemSize={ComponentSize.Medium}
          />
        </div>
      </OutlineTitlePanel>
      <OutlineTitlePanel title="Stadium" className={styles.EditTeamOutlinePanel}>
        <div className={styles.EditTeamGroup}>
          <ASTextField
            value={teamStadium}
            placeholder="Stadium"
            showClearButton
            size={ComponentSize.Medium}
            onChange={setTeamStadium}
            // style={{ maxWidth: '250px', minWidth: 'unset' }}
          />

          <ASTextField
            value={String(stadiumCapacity)}
            placeholder="Capacity"
            size={ComponentSize.Medium}
            type="number"
            onChange={(value: string) => setStadiumCapacity(parseInt(value))}
            // style={{ maxWidth: '175px', minWidth: 'unset' }}
          />
        </div>

        <div className={`${styles.EditTeamGroup}`}>
          <TitledButtonGroup
            title="Stadium Cover"
            buttonItems={stadiumTypeOpts}
            onItemSelected={(roof) => setTeamStadiumHasRoof(stadiumTypeOpts.indexOf(roof) === 1)}
            selectedItem={teamStadiumHasRoof ? stadiumTypeOpts[1] : stadiumTypeOpts[0]}
            itemSize={ComponentSize.Medium}
          />

          <TitledButtonGroup
            title="Field Type"
            buttonItems={fieldTypeOpts}
            onItemSelected={(turf) => setTeamStadiumFieldType(turf as FieldTurfType)}
            selectedItem={teamStadiumFieldType}
            itemSize={ComponentSize.Medium}
          />
        </div>
      </OutlineTitlePanel>

      <OutlineTitlePanel title="Rivals" className={styles.EditTeamOutlinePanel}>
        <div className={`${styles.EditTeamGroup}`}>
          <TeamPicker
            selectedTeams={teamRivalries}
            onSelectionChanged={setTeamRivalries}
            disabledTeams={disabledTeams}
          />
        </div>
      </OutlineTitlePanel>
    </div>
  );
};

export default EditTeam;
